import { useEffect, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Segment, Header, Icon, Message, Button, Popup, Card } from "semantic-ui-react";
import { DataTable, getCommonAPI } from "hpp-fe-common";

import { setActiveSideNavID } from "../../loyaltySideNavSlice";
import {
  redeemableSerialInprogress,
  redeemableSerialProcessed,
} from "./viewRedeemableSerialSlice";
import { signOut } from "../../../auth/authSlice";

const API_PATH = "/api_v1/points/my-redeemables";

const columns = [
  { name: "Serial Number", selector: (row) => row.serialNumber, sortable: true, grow: 2 },
  { name: "Sold By", selector: (row) => row.soldByCompanyName, sortable: true, grow: 2 },
  { name: "SAP Code", selector: (row) => row.sapCode, sortable: true },
  { name: "CN Points", selector: (row) => row.cnPoints, sortable: true },
  { name: "Transaction Points", selector: (row) => row.transactionPoints, sortable: true },
  { name: "Total Points", selector: (row) => row.totalPoints, sortable: true },
  {
    name: "Received Date",
    selector: (row) => row.lastTransactionTime,
    sortable: true,
    grow: 2,
    cell: (row) => (row.lastTransactionTime ? new Date(row.lastTransactionTime).toLocaleString() : "-"),
  },
];

const filterTypes = [
  { key: "serialNumber", text: "By Serial Number", value: "serialNumber" },
  { key: "soldByCompanyName", text: "By Sold By", value: "soldByCompanyName" },
  { key: "sapCode", text: "By SAP Code", value: "sapCode" }
];

function ViewRedeemableSerial() {
  const dispatch = useDispatch();

  const { loading, error, errorMessage, errorMessageData, items } = useSelector(
    (state) => state.viewRedeemableSerial
  );

  const [capData, setCapData] = useState(null);

  useEffect(() => {
    dispatch(setActiveSideNavID("viewRedeemableSerial"));
  }, [dispatch]);

  const refresh = useCallback(() => {
    dispatch(redeemableSerialInprogress());

    const requestURL = encodeURI(`${API_PATH}?portal=inst`);

    getCommonAPI()
      .get(requestURL)
      .then(({ data }) => {
        if (data?.authenticated === false) {
          return dispatch(
            signOut({
              error: true,
              errorMessage: data.errorMessage,
              errorMessageData: data.errorMessageData,
            })
          );
        }

        if (data?.error) {
          dispatch(
            redeemableSerialProcessed({
              error: true,
              errorMessage: data?.errorMessage || "Error loading data",
              errorMessageData: data?.errorMessageData || "",
              items: [],
            })
          );
        } else {
          dispatch(
            redeemableSerialProcessed({
              items: data?.data || [],
            })
          );
        }
      })
      .catch((err) => {
        dispatch(
          redeemableSerialProcessed({
            error: true,
            errorMessage: "Error loading redeemable serials. Please contact system administrator",
            errorMessageData: err.toString(),
            items: [],
          })
        );
      });
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const fetchDailyCap = useCallback(() => {
    getCommonAPI()
      .get("/api_v1/points/promo-daily-cap?portal=inst")
      .then(({ data }) => {
        if (data?.authenticated === false) {
          return dispatch(signOut({ error: true, errorMessage: data.errorMessage }));
        }
        if (!data?.error) {
          setCapData(data?.data || null);
        }
      })
      .catch(() => {
        // Silent fail — cap card is informational only, don't block the page
      });
  }, [dispatch]);

  useEffect(() => {
    fetchDailyCap();
  }, [fetchDailyCap]);

  const refreshViaButton = () => {
    refresh();
    fetchDailyCap();
  };

  const showEmpty = !loading && !error && (!Array.isArray(items) || items.length === 0);

  return (
    <Segment loading={loading}>
      <Header as="h3">
        <Icon name="list" />
        <Header.Content>View Redeemable Serial</Header.Content>
      </Header>

      <Popup
        position="left center"
        content="Refresh data"
        trigger={
          <Button
            color="black"
            onClick={refreshViaButton}
            size="massive"
            style={{ position: "fixed", right: "10px", bottom: "30px", zIndex: 100 }}
            circular
            icon="sync alternate"
          />
        }
      />

      {error && (
        <Message negative>
          <Message.Header>Error loading Redeemable Serials</Message.Header>
          <p>{typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)}</p>
          {errorMessageData && <p>{typeof errorMessageData === 'string' ? errorMessageData : JSON.stringify(errorMessageData)}</p>}
        </Message>
      )}

      {showEmpty && (
        <Message info>
          <Message.Header>No Records</Message.Header>
          <p>No redeemable serials found.</p>
        </Message>
      )}

      {capData?.capConfigured && (
        <Card color={capData?.capSource === "promo" ? "orange" : "blue"} style={{ marginBottom: '1em' }}>
          <Card.Content>
            <Card.Header>
              {capData?.capSource === "promo" ? "Today's Promo Earning Limit" : "Today's Earning Limit"}
            </Card.Header>
            <Card.Meta>Resets at midnight (local time)</Card.Meta>
            <Card.Description>
              <strong>{Math.round(capData.remaining).toLocaleString()}</strong> points remaining today
              <br />
              ({Math.round(capData.todayEarned).toLocaleString()} of {capData.dailyCap.toLocaleString()} earned)
            </Card.Description>
          </Card.Content>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={Array.isArray(items) ? items : []}
        filterFromServer={false}
        filterCaption="Search..."
        filterTypes={filterTypes}
        showTotalFor="totalPoints"
      />
    </Segment>
  );
}

export default ViewRedeemableSerial;
